import type {
	Property,
	ShortVideoItem,
	ShortVideoProperty
} from '~/core/entities/property';

import { floatToLocaleString, replaceNbspWithSpace } from '@/utils';
import { defaultTwitterCardMeta } from '~/constants/seo';
import { AddressUtils } from '~/utils/AddressUtilsWrapper';
import { capitalize, grammarInflection } from '~~/utils';

type GenericProperty = Pick<
	Property | ShortVideoProperty,
	| 'suites'
	| 'bedrooms'
	| 'askingPrice'
	| 'region'
	| 'city'
	| 'state'
	| 'area'
	| 'parkingSpots'
	| 'isExclusive'
> & {
	propertyType?: { name: string };
};

export function formatPropertyFeature({
	value,
	singular,
	plural
}: {
	value: number | null | undefined;
	singular: string;
	plural: string;
}): string {
	if (!value) return '';
	return `${value}${singular === 'm²' ? singular : ` ${grammarInflection(value, singular, plural)}`}`;
}

export const generatePropertySeoTitle = (
	property: GenericProperty | null | undefined,
	suffix = '| PilarHomes'
) => {
	if (!property) return suffix.replace('| ', '');

	const { suites, bedrooms, askingPrice, region, city, state, propertyType } =
		property;

	let benefits = '';
	if (suites && suites > 0) {
		benefits = `com ${suites} ${grammarInflection(suites, 'suíte', 'suítes')}`;
	} else if (bedrooms && bedrooms > 0) {
		benefits = `com ${bedrooms} ${grammarInflection(bedrooms, 'quarto', 'quartos')}`;
	}

	const price = floatToLocaleString(askingPrice ?? 0, {
		style: 'currency'
	});

	const sanitizedPrice = replaceNbspWithSpace(price);
	const typeName = propertyType ? capitalize(propertyType.name) : 'Imóvel';
	const address = AddressUtils.formatAddressDisplay(region, city, state);

	const baseTitle = benefits
		? `${typeName} ${benefits} à venda em ${address} por ${sanitizedPrice}`
		: `${typeName} à venda em ${address} por ${sanitizedPrice}`;

	return `${baseTitle} ${suffix}`;
};

export const generatePropertySeoDescription = (
	property: GenericProperty | null | undefined
) => {
	if (!property)
		return 'Explore as melhores opções de imóveis de alto padrão no PilarHomes.';

	const {
		region,
		city,
		state,
		area,
		bedrooms,
		suites,
		parkingSpots,
		isExclusive,
		propertyType
	} = property;

	const features = [
		formatPropertyFeature({ value: area, singular: 'm²', plural: 'm²' }),
		formatPropertyFeature({
			value: bedrooms,
			singular: 'quarto',
			plural: 'quartos'
		}),
		formatPropertyFeature({
			value: suites,
			singular: 'suíte',
			plural: 'suítes'
		}),
		formatPropertyFeature({
			value: parkingSpots,
			singular: 'vaga',
			plural: 'vagas'
		})
	].filter(Boolean);

	let descriptionParts = '';
	if (features.length > 0) {
		const lastPart = features.pop();
		descriptionParts = features.length
			? `${features.join(', ')} e ${lastPart}`
			: lastPart!;
	}

	const finalPhrase = isExclusive
		? 'Descubra todos os detalhes deste imóvel exclusivo do PilarHomes!'
		: 'Descubra todos os detalhes deste imóvel no PilarHomes!';

	const propertyTypeText = propertyType
		? capitalize(propertyType.name)
		: 'Imóvel';

	const address = AddressUtils.formatAddressDisplay(region, city, state);
	const featuresText = descriptionParts ? `, com ${descriptionParts}` : '';

	return `Seu novo lar em ${region} te espera: ${propertyTypeText} à venda em ${address}${featuresText}. ${finalPhrase}`;
};

export const generatePropertySeoKeywords = (
	property: GenericProperty | null | undefined,
	extraKeywords = ''
) => {
	const baseKeywords = 'alto padrão, à venda, PilarHomes';
	const suffix = extraKeywords ? `, ${extraKeywords}` : '';

	if (!property) return `${baseKeywords}${suffix}`;

	const { region, city, propertyType } = property;
	const typeName = propertyType ? propertyType.name : 'Imóvel';
	return `${typeName}, ${region}, ${city}, ${baseKeywords}${suffix}`;
};

const getSeoTagDetailsProperties = (
	property: Ref<Property | null | undefined>
) => {
	const title = () => generatePropertySeoTitle(property.value);
	const description = () => generatePropertySeoDescription(property.value);
	const keywords = () => generatePropertySeoKeywords(property.value);
	const ogImage = () => property.value?.images?.[0]?.watermarkUrl ?? '';
	return {
		title,
		description,
		keywords,
		ogTitle: title,
		ogDescription: description,
		ogImage,
		twitterTitle: title,
		twitterDescription: description,
		twitterCard: () =>
			defaultTwitterCardMeta as
				| 'summary_large_image'
				| 'summary'
				| 'app'
				| 'player',
		twitterImage: ogImage
	};
};

export const useSeoTagDetails = (
	property: Ref<Property | null | undefined>
) => {
	useSeoMeta(getSeoTagDetailsProperties(property));
};
